import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import {
  getAdminCategories,
  updateCategoryImage,
  toggleCategoryActive,
  updateCategoryName,
  updateCategoryOrder,
  resetCategoryOverride,
  resetAllChannelOverrides,
  getCustomizableChannels,
  updateCustomizableChannels,
  getCategoryProducts
} from "../../api/Products";
import CategoriesUI from "./components/CategoriesUI";

const Categories = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  // States for customizable channels config
  const [customizableChannels, setCustomizableChannels] = useState([]);
  const [loadingCustomizable, setLoadingCustomizable] = useState(false);
  const [savingCustomizable, setSavingCustomizable] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    const response = await getAdminCategories();
    if (response && response.data && response.data.status === "success") {
      setCategories(response.data.categories || []);
      setChannels(response.data.channels || []);
    } else {
      message.error("Error al cargar las categorías");
    }
    setLoading(false);
  }, []);

  const fetchCustomizableChannelsList = async () => {
    setLoadingCustomizable(true);
    const response = await getCustomizableChannels();
    if (response && response.data && response.data.status === "success") {
      setCustomizableChannels(response.data.channels || []);
    } else {
      message.error("Error al cargar la configuración de canales");
    }
    setLoadingCustomizable(false);
  };

  const handleSaveCustomizableChannels = async (channelIds) => {
    setSavingCustomizable(true);
    const response = await updateCustomizableChannels(channelIds);
    let success = false;
    if (response && response.data && response.data.status === "success") {
      message.success("Configuración de canales guardada con éxito");
      await fetchCategories();
      success = true;
    } else {
      message.error("Error al guardar la configuración de canales");
    }
    setSavingCustomizable(false);
    return success;
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleImageUpload = async (id, file, type) => {
    setUpdatingId(id);
    const hideLoading = message.loading(`Subiendo ${type === "banner" ? "banner" : "icono"}...`, 0);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64Str = reader.result;
        const response = await updateCategoryImage(id, base64Str, type);

        hideLoading();
        if (response && response.data && response.data.status === "success") {
          message.success("Imagen actualizada con éxito");
          fetchCategories();
        } else {
          message.error("Error al actualizar la imagen");
        }
        setUpdatingId(null);
      };
      reader.onerror = () => {
        hideLoading();
        message.error("Error al leer el archivo");
        setUpdatingId(null);
      };
    } catch (err) {
      hideLoading();
      message.error("Ocurrió un error inesperado al subir la imagen");
      setUpdatingId(null);
    }
  };

  const handleToggleActive = async (id, active) => {
    const response = await toggleCategoryActive(id, active, selectedChannelId);
    if (response && response.data && response.data.status === "success") {
      message.success(response.data.message);
      fetchCategories();
    } else {
      message.error("Error al cambiar el estado de la categoría");
    }
  };

  const handleResetOverride = async (id) => {
    if (!selectedChannelId) return;
    const response = await resetCategoryOverride(id, selectedChannelId);
    if (response && response.data && response.data.status === "success") {
      message.success(response.data.message);
      fetchCategories();
    } else {
      message.error("Error al restablecer la configuración de la categoría");
    }
  };

  const handleResetAllOverrides = async () => {
    if (!selectedChannelId) return;
    const response = await resetAllChannelOverrides(selectedChannelId);
    if (response && response.data && response.data.status === "success") {
      message.success(response.data.message);
      fetchCategories();
    } else {
      message.error("Error al restablecer los overrides del canal");
    }
  };

  const handleUpdateName = async (id, name) => {
    const response = await updateCategoryName(id, name);
    if (response && response.data && response.data.status === "success") {
      message.success(response.data.message);
      fetchCategories();
    } else {
      message.error("Error al actualizar el nombre de la categoría");
    }
  };

  const getResolvedCategoryState = (cat) => {
    if (!selectedChannelId) {
      return {
        active: cat.active,
        setOrder: cat.setorder || 0
      };
    }
    const channelRule = cat.channels?.find(c => c.channel_id === selectedChannelId);
    if (channelRule) {
      return {
        active: channelRule.active,
        setOrder: channelRule.setOrder !== undefined && channelRule.setOrder !== null ? channelRule.setOrder : (cat.setorder || 0)
      };
    }
    return {
      active: cat.active,
      setOrder: cat.setorder || 0
    };
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const orderA = getResolvedCategoryState(a).setOrder;
    const orderB = getResolvedCategoryState(b).setOrder;
    return orderA - orderB;
  });

  const handleDragDropId = async (draggedId, targetId) => {
    const dragIdx = sortedCategories.findIndex(c => c.id === draggedId);
    const targetIdx = sortedCategories.findIndex(c => c.id === targetId);
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) return;

    const list = [...sortedCategories];
    const [draggedItem] = list.splice(dragIdx, 1);
    list.splice(targetIdx, 0, draggedItem);

    const orderList = list.map((item, idx) => ({
      id: item.id,
      setOrder: idx
    }));

    const updatedCategories = categories.map(c => {
      const orderItem = orderList.find(x => x.id === c.id);
      if (orderItem) {
        if (selectedChannelId) {
          return {
            ...c,
            channels: c.channels.map(ch => ch.channel_id === selectedChannelId ? { ...ch, setOrder: orderItem.setOrder } : ch)
          };
        } else {
          return {
            ...c,
            setorder: orderItem.setOrder
          };
        }
      }
      return c;
    });

    setCategories(updatedCategories);

    try {
      const res = await updateCategoryOrder(selectedChannelId, orderList);
      if (res.error || !res.data || res.data.status !== "success") {
        message.error("¡Hubo un problema actualizando el orden!");
        fetchCategories();
      } else {
        message.success("¡Orden actualizado!");
        fetchCategories();
      }
    } catch (e) {
      message.error("¡Hubo un problema actualizando el orden!");
      fetchCategories();
    }
  };

  return (
    <CategoriesUI
      loading={loading}
      categories={sortedCategories}
      channels={channels}
      selectedChannelId={selectedChannelId}
      onChannelChange={setSelectedChannelId}
      updatingId={updatingId}
      onImageUpload={handleImageUpload}
      onToggleActive={handleToggleActive}
      onResetOverride={handleResetOverride}
      onResetAllOverrides={handleResetAllOverrides}
      onUpdateName={handleUpdateName}
      handleDragDropId={handleDragDropId}
      refresh={fetchCategories}
      customizableChannels={customizableChannels}
      loadingCustomizable={loadingCustomizable}
      savingCustomizable={savingCustomizable}
      fetchCustomizableChannels={fetchCustomizableChannelsList}
      onSaveCustomizableChannels={handleSaveCustomizableChannels}
      fetchCategoryProducts={getCategoryProducts}
    />
  );
};

export default Categories;
